import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { isAuth, getUserStatus, getUserFirstName, getUserLastName, getIsSubscribed } from 'containers/authorization/selectors';
import { logout } from 'containers/authorization/actions';
import Logo from 'assets/svg/FintoolBlack';
import AiSpark from 'assets/svg/AiSpark';
import SearchBar from '../SearchBar';

import { getMobileMenu } from './utils';
import { OutlineNavButton } from '../DesktopHeader/styles';
import UserChip from '../DesktopHeader/Common/UserChip';
import { Container, Navbar, LeftGroup, LogoContainer, RightGroup, AiWrapper, AiButton, AiBadge, List } from './styles';
import HamburgerButton from './Button';
import Item from './Item';

const MobileHeader = ({ resetSearch, onOpenAi, hasNew }) => {
  const { t } = useTranslation('app');
  const [isSignedIn, status, name, surName, isSubscribed] = [
    useSelector(isAuth),
    useSelector(getUserStatus),
    useSelector(getUserFirstName),
    useSelector(getUserLastName),
    useSelector(getIsSubscribed),
  ];
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const handleClick = useCallback(
    (e) => {
      if (e && e.currentTarget.dataset.path === '/videos') resetSearch();
      setOpen(!open);
    },
    [open, resetSearch]
  );
  const handleCloseMenu = useCallback(() => setOpen(false), []);
  const handleLogout = useCallback(() => {
    dispatch(logout());
    handleClick();
  }, [dispatch, handleClick]);

  const handleDropdownLogout = useCallback(() => dispatch(logout()), [dispatch]);

  const handleOpenAi = useCallback(() => {
    onOpenAi();
    handleCloseMenu();
  }, [onOpenAi, handleCloseMenu]);

  const initials = useMemo(() => {
    const first = name?.trim()?.[0] || '';
    const last = surName?.trim()?.[0] || '';

    return `${first}${last}`.toUpperCase();
  }, [name, surName]);

  const list = useMemo(() => getMobileMenu(isSignedIn, status, isSubscribed), [isSignedIn, status, isSubscribed]);

  useEffect(() => {
    if (!open) return () => null;

    const html = document.querySelector('html');
    html.style.overflow = 'hidden';

    return () => {
      html.style.overflow = 'auto';
    };
  }, [open]);

  return (
    <Container open={open}>
      <Navbar open={open}>
        <LeftGroup>
          <HamburgerButton isOpen={open} onClick={handleClick} />
          {isSignedIn && (
            <LogoContainer>
              <Link to="/" aria-label="Fintool">
                <Logo />
              </Link>
            </LogoContainer>
          )}
        </LeftGroup>
        <RightGroup>
          {isSubscribed && (
            <AiWrapper>
              <AiButton type="button" aria-label={t('Fintool AI')} onClick={handleOpenAi}>
                <AiSpark />
              </AiButton>
              <AiBadge $hasNew={hasNew}>NEU</AiBadge>
            </AiWrapper>
          )}
          {!isSignedIn && (
            <>
              <OutlineNavButton to="/anmelden">{t('Login')}</OutlineNavButton>
              <OutlineNavButton to="/registrieren">{t('Register')}</OutlineNavButton>
            </>
          )}
          {isSignedIn && (
            <UserChip initials={initials} firstName={name || ''} status={status} onLogout={handleDropdownLogout} isMobile />
          )}
        </RightGroup>
      </Navbar>
      <SearchBar />
      <List $isOpen={open}>
        {list.map(({ key, name: btnName, link, matches, isHightLight, isExternal }) => {
          if (key === 'ai')
            return (
              <Item
                key={key}
                name={t(btnName)}
                onClick={() => {
                  onOpenAi();
                  handleCloseMenu();
                }}
              />
            );
          if (key === 'logout') return <Item key={key} name={t(btnName)} onClick={handleLogout} />;
          return (
            <Item
              key={key}
              name={t(btnName)}
              onClick={handleClick}
              to={link}
              matches={matches}
              isHightLight={isHightLight}
              isExternal={isExternal}
            />
          );
        })}
      </List>
    </Container>
  );
};

MobileHeader.propTypes = {
  resetSearch: PropTypes.func.isRequired,
  onOpenAi: PropTypes.func.isRequired,
  hasNew: PropTypes.bool,
};

MobileHeader.defaultProps = { hasNew: false };

export default MobileHeader;
